import React, { FC, useCallback, useEffect, useRef, useReducer } from 'react';
import classNames from 'classnames';
import { eventCenter } from '@tarojs/taro';
import View from '../view';
import { useDidShow, useDidHide } from '../../common/hooks';
import Loading from '../loading';
import Icon from '../icon';
import Popup from '../popup';
import statusImg from './img.json';

type IToastStatus = 'fail' | 'loading' | 'success';

interface IToastState {
  text: string;
  icon: string;
  mask: boolean;
  status?: IToastStatus;
  visible?: boolean;
}

interface IToastEventOptions extends Omit<IToastState, 'visible'> {
  type: 'open' | 'loading' | 'fail' | 'success' | 'info' | 'clear' | 'setData';
  duration?: number;
}

interface IToastProps {
  className?: string;
}

const EVENT_NAME = 'ink-toast';

const INITIAL_STATE: IToastState = {
  text: '',
  icon: '',
  mask: true,
  status: undefined,
  visible: false,
};

const open = (option?: Partial<Pick<IToastEventOptions, 'duration' | 'mask' | 'text' | 'icon' | 'status'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'open' });
};

const info = (option?: Partial<Pick<IToastEventOptions, 'duration' | 'mask' | 'text'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'info' });
};

const success = (option?: Partial<Pick<IToastEventOptions, 'duration' | 'mask' | 'text'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'success' });
};

const fail = (option?: Partial<Pick<IToastEventOptions, 'duration' | 'mask' | 'text'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'fail' });
};

const loading = (option?: Partial<Pick<IToastEventOptions, 'duration' | 'mask' | 'text'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'loading' });
};

const clear = () => {
  eventCenter.trigger(EVENT_NAME, { type: 'clear' });
};

const setData = (option?: Partial<Pick<IToastEventOptions, 'mask' | 'text' | 'icon' | 'status'>>) => {
  eventCenter.trigger(EVENT_NAME, { ...option, type: 'setData' });
};

const Toast: FC<IToastProps> & { open: typeof open } & { info: typeof info } & { success: typeof success } & { fail: typeof fail } & { loading: typeof loading } & { clear: typeof clear } & {
  setData: typeof setData;
} = ({ className }) => {
  const [data, dispatch] = useReducer((state: IToastState, action: { type: string; payload?: Partial<IToastState> }) => {
    switch (action.type) {
      case 'data/reset':
        return { ...INITIAL_STATE };
      case 'data/set':
        return { ...state, ...action.payload };
      case 'data/set-open':
        return { ...INITIAL_STATE, ...action.payload, visible: true };
      case 'data/set-info':
        return { ...INITIAL_STATE, ...action.payload, status: undefined, icon: '', visible: true };
      case 'data/set-success':
        return { ...INITIAL_STATE, ...action.payload, status: 'success', icon: '', visible: true };
      case 'data/set-fail':
        return { ...INITIAL_STATE, ...action.payload, status: 'fail', icon: '', visible: true };
      case 'data/set-loading':
        return { ...INITIAL_STATE, ...action.payload, status: 'loading', icon: '', visible: true };
      default:
        return state;
    }
  }, INITIAL_STATE);

  const { icon, status, mask, text, visible } = data;

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const realIcon = icon || (status === 'loading' ? 'loading' : statusImg[status!]);
  const rootClass = classNames(
    'ink-toast',
    {
      'ink-toast--empty': !realIcon && !text,
      'ink-toast--only-text': !realIcon && text,
      'ink-toast--icon': !!realIcon,
    },
    className,
  );
  const maskClass = classNames('ink-toast__mask', {
    'ink-toast__mask--none': !mask,
  });
  const infoClass = classNames('ink-toast__info', {
    'ink-toast__info--no-icon': !realIcon,
  });

  const cleanTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const bindMessageListener = useCallback(() => {
    eventCenter.on(EVENT_NAME, ({ type, duration = 1700, ...restOptions }: Partial<IToastEventOptions> = {}) => {
      cleanTimer();
      switch (type) {
        case 'clear':
          dispatch({ type: 'data/reset' });
          break;
        case 'setData':
          dispatch({ type: 'data/set', payload: restOptions });
          break;
        case 'open':
          dispatch({ type: 'data/set-open', payload: restOptions });
          break;
        case 'info':
          dispatch({ type: 'data/set-info', payload: restOptions });
          break;
        case 'success':
          dispatch({ type: 'data/set-success', payload: restOptions });
          break;
        case 'fail':
          dispatch({ type: 'data/set-fail', payload: restOptions });
          break;
        case 'loading':
          dispatch({ type: 'data/set-loading', payload: restOptions });
          break;
        default:
          throw new Error('invaild type');
      }
      if (!['clear', 'setData'].includes(type)) {
        if (duration > 0) {
          timerRef.current = setTimeout(() => {
            dispatch({ type: 'data/reset', payload: { visible: false } });
          }, duration);
        }
      }
    });
  }, []);

  useDidShow(() => {
    bindMessageListener();
  });

  useDidHide(() => {
    eventCenter.off(EVENT_NAME);
  });

  useEffect(() => {
    bindMessageListener();
    return () => {
      cleanTimer();
      eventCenter.off(EVENT_NAME);
    };
  }, [bindMessageListener]);

  return (
    <Popup visible={visible} className={rootClass} maskClassName={maskClass} maskClosable={false}>
      {realIcon && (realIcon === 'loading' ? <Loading color="#ffffff" size={64} /> : <Icon color="#ffffff" name={realIcon} size={64} />)}
      {text && <View className={infoClass}>{text}</View>}
    </Popup>
  );
};

Toast.open = open;

Toast.info = info;

Toast.success = success;

Toast.fail = fail;

Toast.loading = loading;

Toast.clear = clear;

Toast.setData = setData;

export default Toast;
